// Marketing · Presupuesto — tope mensual y reparto por canal de la org.
// GET/PUT /api/v1/admin/marketing/presupuesto?mes=YYYY-MM (scope marketing:budget).
// El usuario captura PESOS; al backend van CENTAVOS (enteros).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Save, Trash2 } from 'lucide-react';

import { Button, Card, ErrorNote, PageHeader } from '@/modules/orion-shell/ui';

import { demoActiva, fetchPresupuesto, savePresupuesto, type Presupuesto } from '../api';
import { CANAL, canalLabel, DemoChip, dinero, EstadoCarga, hoyCivil, mesLargo } from '../shared';

function mover(mes: string, n: number): string {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

const aPesos = (cents: number) => (cents / 100).toString();
function aCentavos(pesos: string): number | null {
  const limpio = pesos.replace(/[$,\s]/g, '');
  if (limpio === '') return 0;
  const v = Number(limpio);
  if (!Number.isFinite(v) || v < 0) return null;
  return Math.round(v * 100);
}

type Fila = { canal: string; pesos: string };

export function PresupuestoView({ addNotification }: { addNotification?: (n: { type: string; title: string; message?: string }) => void }) {
  const [mes, setMes] = useState(hoyCivil().slice(0, 7));
  const [base, setBase] = useState<Presupuesto | null>(null);
  const [tope, setTope] = useState('');
  const [filas, setFilas] = useState<Fila[]>([]);
  const [alertas, setAlertas] = useState('80, 100');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [errorGuardar, setErrorGuardar] = useState('');
  const [demo, setDemoState] = useState(demoActiva());

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAviso('');
    try {
      const p = await fetchPresupuesto(mes);
      setBase(p);
      setTope(aPesos(p.tope_cents));
      setFilas(p.por_canal.map((c) => ({ canal: c.canal, pesos: aPesos(c.tope_cents) })));
      setAlertas((p.alertas_pct?.length ? p.alertas_pct : [80, 100]).join(', '));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
      setDemoState(demoActiva());
    }
  }, [mes]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const topeCents = aCentavos(tope);
  const filasCents = filas.map((f) => aCentavos(f.pesos));
  const repartido = filasCents.reduce<number>((a, v) => a + (v ?? 0), 0);
  const alertasPct = alertas.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0 && n <= 200);

  const problema = useMemo(() => {
    if (topeCents == null) return 'El tope del mes debe ser un número mayor o igual a 0.';
    if (filasCents.some((v) => v == null)) return 'Revisa los montos por canal: deben ser números mayores o iguales a 0.';
    if (new Set(filas.map((f) => f.canal)).size !== filas.length) return 'Hay un canal repetido.';
    if (repartido > topeCents) return `El reparto por canal (${dinero(repartido)}) supera el tope del mes (${dinero(topeCents)}).`;
    return '';
  }, [topeCents, filasCents, filas, repartido]);

  const canalesLibres = Object.keys(CANAL).filter((c) => !filas.some((f) => f.canal === c));

  async function guardar() {
    if (problema || topeCents == null || !base) return;
    setGuardando(true);
    setAviso('');
    setErrorGuardar('');
    try {
      const nuevo = await savePresupuesto({
        mes,
        moneda: base.moneda || 'MXN',
        tope_cents: topeCents,
        por_canal: filas.map((f, i) => ({ canal: f.canal, tope_cents: filasCents[i] ?? 0 })),
        alertas_pct: alertasPct.length ? alertasPct : [80, 100],
      });
      setBase(nuevo);
      setAviso('Presupuesto guardado.');
      addNotification?.({ type: 'success', title: 'Presupuesto guardado', message: mesLargo(mes) });
    } catch (e) {
      setErrorGuardar(e instanceof Error ? e.message : 'No se pudo guardar el presupuesto.');
    } finally {
      setGuardando(false);
    }
  }

  const moneda = base?.moneda || 'MXN';

  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <PageHeader
        title="Presupuesto"
        subtitle="Cuánto puede invertir tu organización en marketing cada mes y cómo se reparte por canal."
        actions={demo ? <DemoChip /> : undefined}
      />

      <div className="mb-4 flex items-center justify-between gap-2 rounded-[20px] border border-border bg-card px-2 py-1.5">
        <Button variant="ghost" onClick={() => setMes(mover(mes, -1))} aria-label="Mes anterior">
          <ChevronLeft size={16} aria-hidden />
        </Button>
        <span className="text-sm font-semibold text-foreground">{mesLargo(mes)}</span>
        <Button variant="ghost" onClick={() => setMes(mover(mes, 1))} aria-label="Mes siguiente">
          <ChevronRight size={16} aria-hidden />
        </Button>
      </div>

      <EstadoCarga loading={loading} error={error} onRetry={() => void cargar()} onDemo={() => void cargar()}>
        <div className="space-y-4">
          <Card title="Tope del mes">
            <label className="block text-sm text-muted-foreground" htmlFor="mkt-tope">
              Inversión máxima en {moneda}
            </label>
            <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-input bg-background px-3 focus-within:ring-2 focus-within:ring-ring">
              <span className="text-muted-foreground" aria-hidden>$</span>
              <input
                id="mkt-tope"
                inputMode="decimal"
                value={tope}
                onChange={(e) => setTope(e.target.value)}
                className="w-full bg-transparent py-2.5 text-lg font-semibold tabular-nums text-foreground outline-none"
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              El agente no puede pasarse de este tope sin tu aprobación. Repartido: {dinero(repartido, moneda)}
              {topeCents != null && topeCents > repartido ? ` · sin asignar ${dinero(topeCents - repartido, moneda)}` : ''}.
            </p>
          </Card>

          <Card
            title="Reparto por canal"
            action={
              canalesLibres.length > 0 && (
                <Button
                  variant="ghost"
                  onClick={() => setFilas([...filas, { canal: canalesLibres[0], pesos: '0' }])}
                >
                  <Plus size={14} aria-hidden /> Agregar canal
                </Button>
              )
            }
          >
            {filas.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin reparto: todo el tope queda disponible para cualquier canal.</p>
            ) : (
              <ul className="space-y-3">
                {filas.map((f, i) => (
                  <li key={i} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center">
                    <select
                      aria-label="Canal"
                      value={f.canal}
                      onChange={(e) => setFilas(filas.map((x, j) => (j === i ? { ...x, canal: e.target.value } : x)))}
                      className="col-span-2 min-w-0 rounded-xl border border-input bg-background px-3 py-2 text-sm text-foreground sm:col-span-1"
                    >
                      {[f.canal, ...canalesLibres].map((c) => (
                        <option key={c} value={c}>{canalLabel(c)}</option>
                      ))}
                    </select>
                    <div className="flex min-w-0 items-center gap-2 rounded-xl border border-input bg-background px-3 focus-within:ring-2 focus-within:ring-ring">
                      <span className="text-muted-foreground" aria-hidden>$</span>
                      <input
                        aria-label={`Tope de ${canalLabel(f.canal)}`}
                        inputMode="decimal"
                        value={f.pesos}
                        onChange={(e) => setFilas(filas.map((x, j) => (j === i ? { ...x, pesos: e.target.value } : x)))}
                        className="w-full min-w-0 bg-transparent py-2 text-sm tabular-nums text-foreground outline-none"
                      />
                    </div>
                    <Button variant="ghost" onClick={() => setFilas(filas.filter((_, j) => j !== i))} aria-label={`Quitar ${canalLabel(f.canal)}`}>
                      <Trash2 size={16} aria-hidden />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Avisos de gasto">
            <label className="block text-sm text-muted-foreground" htmlFor="mkt-alertas">
              Avisar al llegar a estos porcentajes del tope
            </label>
            <input
              id="mkt-alertas"
              value={alertas}
              onChange={(e) => setAlertas(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="mt-2 text-xs text-muted-foreground">Separados por coma. Por ejemplo: 80, 100.</p>
          </Card>

          {problema && <ErrorNote>{problema}</ErrorNote>}
          {errorGuardar && <ErrorNote>{errorGuardar}</ErrorNote>}
          {aviso && <p className="rounded-lg bg-primary/10 px-3 py-2 text-xs text-primary" role="status">{aviso}</p>}

          <div className="flex justify-end">
            <Button variant="brand" onClick={() => void guardar()} disabled={Boolean(problema) || guardando || !base}>
              <Save size={16} aria-hidden /> {guardando ? 'Guardando…' : 'Guardar presupuesto'}
            </Button>
          </div>
        </div>
      </EstadoCarga>
    </div>
  );
}
